import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  type FieldValues,
  type SubmitHandler,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form';
import type { z } from 'zod';
import { cn } from '../cn';
import { Label } from './primitives';

/**
 * react-hook-form bound to a zod schema from @marketingbee/contracts.
 *   const form = useZodForm(createContactInput, { defaultValues: {...} });
 */
export function useZodForm<S extends z.ZodType<FieldValues, FieldValues>>(
  schema: S,
  props: Omit<UseFormProps<z.input<S>, unknown, z.output<S>>, 'resolver'> = {},
): UseFormReturn<z.input<S>, unknown, z.output<S>> {
  return useForm<z.input<S>, unknown, z.output<S>>({
    ...props,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any) as any,
  });
}

/** <form> wired to a form instance; provides context to <FormField>. */
export function Form<TValues extends FieldValues, TOut extends FieldValues>({
  form,
  onSubmit,
  children,
  className,
  ...props
}: Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  form: UseFormReturn<TValues, unknown, TOut>;
  onSubmit: SubmitHandler<TOut>;
}) {
  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={form.handleSubmit(onSubmit)} className={cn('space-y-5', className)} {...props}>
        {children}
      </form>
    </FormProvider>
  );
}

function getError(errors: Record<string, unknown>, name: string): string | undefined {
  let cur: unknown = errors;
  for (const part of name.split('.')) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  const msg = cur && typeof cur === 'object' && 'message' in cur ? (cur as { message?: unknown }).message : undefined;
  return typeof msg === 'string' ? msg : undefined;
}

/**
 * Label + control + description + validation error. The control gets `id`, `aria-invalid` and
 * `aria-describedby`. For simple inputs pass `name` and children receive `register(name)` props:
 *
 *   <FormField name="email" label="Email"><Input type="email" /></FormField>
 *
 * For custom controls use `render={(field) => ...}` with react-hook-form's Controller instead.
 */
export function FormField({
  name,
  label,
  description,
  required,
  children,
  className,
}: {
  name: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  required?: boolean;
  children: React.ReactElement<Record<string, unknown>>;
  className?: string;
}) {
  const form = useFormContext();
  const id = React.useId();
  const error = getError(form.formState.errors as Record<string, unknown>, name);
  const describedBy = [description ? `${id}-desc` : null, error ? `${id}-err` : null].filter(Boolean).join(' ') || undefined;
  const control = React.cloneElement(children, {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
    ...form.register(name),
  });
  return (
    <div className={cn('space-y-1.5', className)}>
      {label ? (
        <Label htmlFor={id}>
          {label}
          {required ? <span className="text-destructive"> *</span> : null}
        </Label>
      ) : null}
      {control}
      {description ? (
        <p id={`${id}-desc`} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-err`} className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Field wrapper for controls not driven by react-hook-form. */
export function Field({
  label,
  htmlFor,
  description,
  error,
  required,
  children,
  className,
}: {
  label?: React.ReactNode;
  htmlFor?: string;
  description?: React.ReactNode;
  error?: string | null;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label ? (
        <Label htmlFor={htmlFor}>
          {label}
          {required ? <span className="text-destructive"> *</span> : null}
        </Label>
      ) : null}
      {children}
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/** Root-level form error (e.g. API error message). */
export function FormError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-lg bg-destructive/[0.07] px-4 py-3 text-sm text-destructive">
      {message}
    </p>
  );
}

export function FormActions({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('flex items-center justify-end gap-2 pt-3', className)}>{children}</div>;
}
